import { LogOut, RefreshCw } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { QUEUE_ITEM, canSee, visibleNav } from "../domain/access";
import { syncNow } from "../offline/syncState";
import { CommandPalette } from "./CommandPalette";
import { CommandProvider, useRegisterCommands, type Command } from "./commands";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { SyncAgent } from "../offline/SyncAgent";
import { NAV_ICON } from "./navIcons";
import { createContext, useContext } from "react";

const PaletteCtx = createContext<() => void>(() => undefined);
/** Opens the ⌘K palette (for the top-bar search trigger). */
export const useOpenPalette = () => useContext(PaletteCtx);

/** Everything a signed-in tenant screen sits in: the impersonation banner above the shell,
 * the command registry and the ⌘K palette. Shells fill the remaining height. */
export function AppFrame({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <CommandProvider>
      <PaletteCtx.Provider value={() => setOpen(true)}>
        <div className="flex h-dvh flex-col">
          <ImpersonationBanner />
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>
        <ShellCommands />
        <SyncAgent />
        <CommandPalette open={open} onOpenChange={setOpen} />
      </PaletteCtx.Provider>
    </CommandProvider>
  );
}

function ShellCommands() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const cmds = useMemo<Command[]>(() => {
    const nav = visibleNav(user).map((i) => ({
      id: `nav:${i.key}`,
      label: i.moreLabel,
      group: "Go to",
      icon: NAV_ICON[i.key],
      keywords: i.keywords,
      run: () => navigate(i.path),
    }));
    if (canSee(user, QUEUE_ITEM))
      nav.push({
        id: "nav:queue",
        label: QUEUE_ITEM.label,
        group: "Go to",
        icon: NAV_ICON.queue,
        keywords: QUEUE_ITEM.keywords,
        run: () => navigate(QUEUE_ITEM.path),
      });
    return [
      ...nav,
      {
        id: "act:sync",
        label: "Sync now",
        group: "Actions",
        icon: <RefreshCw />,
        keywords: "offline upload queue",
        run: () => void syncNow(),
      },
      {
        id: "act:logout",
        label: "Log out",
        group: "Account",
        icon: <LogOut />,
        keywords: "sign out exit",
        run: logout,
      },
    ];
  }, [user, navigate, logout]);
  useRegisterCommands(cmds);
  return null;
}
